import React, { useState, useRef, useEffect } from "react";
import {
  AsciiPanel,
  AsciiButton,
  AsciiStatusBadge,
  AsciiLoader,
} from "@/components/ui/ascii-theme";

interface VideoPlayerProps {
  videoId?: string;
  originalFilename?: string;
  onError?: (error: any) => void;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  videoId,
  originalFilename,
  onError,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formatError, setFormatError] = useState<any>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const streamUrl = videoId ? `/api/videos/${videoId}/stream` : null;

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    setFormatError(null);

    // Check video compatibility first
    if (videoId) {
      checkVideoCompatibility();
    }

    // Add a small delay to allow proper initialization across all browsers
    if (videoRef.current && videoId) {
      const timer = setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.load();
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [videoId]);

  const checkVideoCompatibility = async () => {
    if (!videoId) return;

    try {
      const response = await fetch(`/api/videos/${videoId}/stream`, {
        method: "HEAD", // Just check headers, don't download content
        credentials: 'include',
      });

      if (response.status === 415) {
        // Unsupported Media Type - get the error details
        const errorResponse = await fetch(`/api/videos/${videoId}/stream`, {
          credentials: 'include'
        });
        const errorData = await errorResponse.json();
        setFormatError(errorData);
        setError(
          errorData.message || "Video format not supported by your browser"
        );
        setIsLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (err) {
      console.error("Video compatibility check failed:", err);
      // Continue with normal loading if compatibility check fails
    }
  };

  // Handle network state changes for all browsers
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleNetworkStateChange = () => {
      if (video.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) {
        console.warn("No video source available");
        setError("Video source not available");
      }
    };

    video.addEventListener("loadstart", handleNetworkStateChange);

    return () => {
      video.removeEventListener("loadstart", handleNetworkStateChange);
    };
  }, [videoId]);

  const handleVideoLoad = () => {
    setIsLoading(false);
    setError(null);
  };

  const handleVideoError = (e: any) => {
    console.error("Video error:", e);

    // Get detailed error information for all browsers
    const video = videoRef.current;
    let errorMessage = "Failed to load video";

    if (video?.error) {
      switch (video.error.code) {
        case MediaError.MEDIA_ERR_ABORTED:
          errorMessage = "Video loading was aborted";
          break;
        case MediaError.MEDIA_ERR_NETWORK:
          errorMessage = "Network error occurred while loading video";
          break;
        case MediaError.MEDIA_ERR_DECODE:
          errorMessage = "Video format not supported by browser";
          break;
        case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
          errorMessage = "Video source not supported";
          break;
        default:
          errorMessage = "Unknown video error occurred";
      }
    }

    setError(errorMessage);
    setIsLoading(false);

    if (onError) {
      onError(e);
    }
  };

  const handleVideoLoadStart = () => {
    setIsLoading(true);
    setError(null);
  };

  const handleVideoCanPlay = () => {
    setIsLoading(false);
    setError(null);
  };

  const handleRetry = () => {
    setError(null);
    setIsLoading(true);

    if (videoRef.current) {
      // Force reload the video element
      const currentTime = videoRef.current.currentTime;
      videoRef.current.load();

      // Restore playback position if it was playing
      if (currentTime > 0) {
        videoRef.current.addEventListener(
          "loadedmetadata",
          () => {
            if (videoRef.current) {
              videoRef.current.currentTime = currentTime;
            }
          },
          { once: true }
        );
      }
    }
  };

  if (!videoId) {
    return (
      <AsciiPanel title="═══ VIDEO PLAYER ═══" className="w-full">
        <div className="flex items-center justify-center h-48 sm:h-64">
          <div className="text-center opacity-60">
            <pre className="text-2xl sm:text-4xl mb-4">▶</pre>
            <p className="text-xs sm:text-sm mb-2 font-bold uppercase">
              No video selected
            </p>
            <p className="text-xs opacity-60">
              Choose a video from the library to start playing
            </p>
          </div>
        </div>
      </AsciiPanel>
    );
  }

  return (
    <AsciiPanel title="═══ VIDEO PLAYER ═══" className="w-full">
      <div className="space-y-3 sm:space-y-4">
        {/* Video Info Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-b-2 border-black pb-2">
          <div className="text-xs sm:text-sm font-bold uppercase truncate flex-1 min-w-0">
            {originalFilename || "Video Player"}
          </div>
          <div className="flex items-center justify-between sm:justify-end gap-2 shrink-0">
            <AsciiStatusBadge
              status={isLoading ? "active" : error ? "error" : "success"}
            >
              {isLoading ? "LOADING" : error ? "ERROR" : "READY"}
            </AsciiStatusBadge>
            {videoId && (
              <a href={`/api/videos/${videoId}/download`} download>
                <AsciiButton variant="default" size="sm">
                  ⬇ DOWNLOAD
                </AsciiButton>
              </a>
            )}
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="border-2 border-red-600 p-2 sm:p-3 bg-red-50">
            <div className="space-y-2">
              <div className="text-xs sm:text-sm font-bold text-red-600">
                ✗ ERROR: {error}
              </div>

              {formatError && (
                <div className="space-y-2 text-xs opacity-60">
                  <div>
                    <span className="font-bold">CURRENT FORMAT:</span>{" "}
                    {formatError.current_format}
                  </div>
                  <div>
                    <span className="font-bold">SUPPORTED FORMATS:</span>{" "}
                    {formatError.supported_formats?.join(", ")}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 pt-2">
                    {formatError.download_url && (
                      <a href={formatError.download_url} download>
                        <AsciiButton variant="default" size="sm">
                          DOWNLOAD VIDEO
                        </AsciiButton>
                      </a>
                    )}
                    <div className="text-xs">
                      Try Chrome, Firefox, or Edge for WebM support
                    </div>
                  </div>
                </div>
              )}

              {!formatError && (
                <AsciiButton
                  variant="default"
                  size="sm"
                  onClick={handleRetry}
                >
                  ⟳ RETRY
                </AsciiButton>
              )}
            </div>
          </div>
        )}

        {/* Video Container */}
        <div className="relative bg-black border-2 border-black">
          <video
            ref={videoRef}
            src={streamUrl || undefined}
            controls
            preload="metadata"
            playsInline
            webkit-playsinline="true"
            muted={false}
            crossOrigin="anonymous"
            autoPlay={false}
            className={`w-full h-auto ${error ? "hidden" : "block"}`}
            onLoadStart={handleVideoLoadStart}
            onLoadedData={handleVideoLoad}
            onCanPlay={handleVideoCanPlay}
            onError={handleVideoError}
            onLoadedMetadata={() => {
              const video = videoRef.current;
              if (video && video.videoWidth === 0 && video.videoHeight === 0) {
                console.warn("Video metadata indicates no video stream");
              }
            }}
            onStalled={() => {
              console.warn("Video playback stalled");
            }}
            onSuspend={() => {
              console.warn("Video loading suspended");
            }}
            onWaiting={() => {
              setIsLoading(true);
            }}
            onPlaying={() => {
              setIsLoading(false);
              setError(null);
            }}
            style={{
              maxHeight: "60vh",
              backgroundColor: "#000",
            }}
          >
            {streamUrl && <source src={streamUrl} type="video/mp4" />}
            <p className="text-center text-white p-4 text-xs">
              Your browser doesn't support HTML5 video.{" "}
              <a
                href={`/api/videos/${videoId}/download`}
                className="underline"
              >
                Download the video
              </a>{" "}
              instead.
            </p>
          </video>

          {/* Loading Overlay */}
          {isLoading && !error && (
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-80">
              <div className="text-center px-4 text-white">
                <AsciiLoader text="Loading video" className="text-white" />
              </div>
            </div>
          )}
        </div>
      </div>
    </AsciiPanel>
  );
};

export default VideoPlayer;
